/**
 * PlanningCurrencyContext
 *
 * Purpose: Lightweight context for sharing the planning currency ISO code
 *          between TemplateForm and field renderers (e.g., CurrencyFieldRenderer)
 * Architecture: Standalone context to avoid circular dependencies between
 *               template-form and metadata-form modules
 * Dependencies: React (createContext, useContext)
 * Author: Development Team
 * Created: 2026-03-09
 */

import { createContext, useContext } from 'react';

/**
 * Context holding the current planning currency ISO 4217 code (e.g., 'USD', 'EUR').
 * null when no currency is selected or when rendered outside a TemplateForm.
 */
const PlanningCurrencyContext = createContext<string | null>(null);

/**
 * usePlanningCurrency — hook to read the planning currency code
 *
 * Purpose: Provides the ISO 4217 currency code set by the active template form
 * Logic: Returns null when used outside a TemplateForm (safe to call anywhere)
 * Output: Currency code string (e.g., 'EUR') or null
 */
export const usePlanningCurrency = (): string | null => {
  return useContext(PlanningCurrencyContext);
};

export const PlanningCurrencyProvider = PlanningCurrencyContext.Provider;
